import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  PlayCircle, StopCircle, Edit, Trash2, ExternalLink, Copy, Users, Activity,
  Clock, MapPin, Hash, MessageSquare, Video, Download, UserPlus, CalendarPlus, Globe,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { getSessionApi } from "../../utils/demoApi";
import CommonToaster from "../Common/CommonToaster";
import {
  PageHeader, AppButton, ConfirmModal, EmptyState, StatusBadge, StatsCard, StatsGrid,
  Tabs, TabsList, TabsTrigger, TabsContent, DetailRow, DetailSection, Paginator,
} from "../ui";
import { resolveSessionKind } from "./sessionKind";
import { can } from "../../utils/access";
import AttendancePanel from "./event/AttendancePanel";
import AutomationPanel from "./event/AutomationPanel";
import RecordingsPanel from "./event/RecordingsPanel";
import "./demo-classes.scss";

const PAGE_SIZE = 20;

/* The attendee permissions, described in a sentence rather than as six
   Yes/No rows. Someone opening this page wants "can the audience talk?", and a
   grid of booleans makes them assemble the answer themselves.

   Defaults mirror `utils/attendeePolicy.js` on the backend, because a session
   created before the field existed has no `attendeePolicy` at all and must
   still be described accurately — as the mic-off, chat-on session it is. */
const describeAttendeePolicy = (policy) => {
  const p = policy || {};
  const on = (key, fallback) => (p[key] === undefined ? fallback : !!p[key]);
  const allowed = [
    on("allowMicrophone", false) && "microphone",
    on("allowCamera", false) && "camera",
    on("allowScreenShare", false) && "screen share",
    on("allowChat", true) && "chat",
    on("allowRaiseHand", true) && "raise hand",
  ].filter(Boolean);
  if (!allowed.length) return "Watch and listen only";
  return allowed.join(", ").replace(/, ([^,]*)$/, " and $1");
};

const getDemoSession = (data) => {
  const session = data?.session || data?.item || data;
  return data?.stats ? { ...session, ...data.stats } : session;
};

// Demo-session states come from ui/statusVocabulary.js. The local
// STATUS_BADGE + STATUS_LABEL pair this replaces was one of THREE identical
// copies (the other two in DemoClasses/DemoClassesDetailPage and
// Dashboard/TrainerDashboard/cards/DemoSessionsCard), and all three mapped
// `live` to the success green while passing a `dot` that rendered nothing.

const formatDateTime = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
};
const formatDuration = (s) => {
  if (!s || s < 1) return "—";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return m ? `${m}m ${sec}s` : `${sec}s`;
};

/** Detail + registrants for BOTH live-session products. `basePath` still
 *  overrides the route tree for the instructor-scoped (`/i/...`) mounts. */
const DemoClassesDetailPage = ({ kind = "demo", basePath, isInstructor = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const vocab = resolveSessionKind(kind);
  const routeBase = basePath || vocab.adminBase;
  const canManage = can("liveEvents.manage");
  const canRun = isInstructor || canManage;

  const [session, setSession] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [confirm, setConfirm] = useState(null); // { type: 'cancel'|'end'|'delete' }

  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsLoading, setLeadsLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`${vocab.apiBase}/sessions/${id}`);
      setSession(getDemoSession(data));
      setWebsiteUrl(data?.websiteUrl || null);
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Failed to load.", type: "error" });
    }
    setLoading(false);
  }, [id, vocab]);

  useEffect(() => { refresh(); }, [refresh]);

  const notify = useCallback((message, type = "success") => setToast({ show: true, message, type }), []);

  const loadLeads = useCallback(async () => {
    setLeadsLoading(true);
    try {
      const { data } = await apiClient.get(`${vocab.apiBase}/sessions/${id}/leads`, {
        params: { page: leadsPage, pageSize: PAGE_SIZE },
      });
      setLeads(data.items || []);
      setLeadsTotal(data.total || 0);
    } catch {
      setToast({ show: true, message: "Failed to load registrants.", type: "error" });
    }
    setLeadsLoading(false);
  }, [id, leadsPage, vocab]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const publicUrl = useMemo(
    () => session?.publicSlug ? `${window.location.origin}${vocab.publicBase}/${session.publicSlug}` : "",
    [session, vocab]
  );

  const copyPublicLink = () => {
    if (!publicUrl) return;
    try {
      navigator.clipboard?.writeText(publicUrl);
      setToast({ show: true, message: "Public link copied.", type: "success" });
    } catch {
      setToast({ show: true, message: publicUrl, type: "info" });
    }
  };

  const downloadCsv = async () => {
    try {
      const { data } = await apiClient.get(`${vocab.apiBase}/sessions/${id}/leads.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${vocab.kind}-${session?.publicSlug || id}-registrants.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Export failed.", type: "error" });
    }
  };

  const calendarUrl = useMemo(
    () => session?.publicSlug ? getSessionApi(vocab.kind).calendarUrl(session.publicSlug) : "",
    [session, vocab]
  );

  const convertLead = async (leadId) => {
    try {
      await apiClient.post(`${vocab.apiBase}/sessions/${id}/leads/${leadId}/convert`);
      setToast({ show: true, message: "Lead pushed to enquiries.", type: "success" });
      loadLeads();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Convert failed.", type: "error" });
    }
  };

  const startSession = async () => {
    setBusy(true);
    try {
      await apiClient.post(`${vocab.apiBase}/sessions/${id}/start`);
      setToast({ show: true, message: `${vocab.FullNoun} started — host console opening shortly.`, type: "success" });
      await refresh();
      setTimeout(() => navigate(`${routeBase}/${id}/host`), 400);
    } catch (err) {
      // The BE answers `errorCode`; `error` was the older shape.
      const code = err?.response?.data?.errorCode || err?.response?.data?.error;
      const msg  = err?.response?.data?.message
        || (code === "NO_HOSTS_AVAILABLE"
          ? "No Zoom hosts available right now. Try again in a few minutes."
          : `Failed to start the ${vocab.noun}.`);
      setToast({ show: true, message: msg, type: "error" });
    }
    setBusy(false);
  };

  const endSession = async () => {
    setBusy(true);
    try {
      await apiClient.post(`${vocab.apiBase}/sessions/${id}/end`);
      setToast({ show: true, message: `${vocab.FullNoun} ended.`, type: "success" });
      setConfirm(null);
      await refresh();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || `Failed to end the ${vocab.noun}.`, type: "error" });
    }
    setBusy(false);
  };

  const cancelSession = async () => {
    setBusy(true);
    try {
      await apiClient.post(`${vocab.apiBase}/sessions/${id}/cancel`);
      setToast({ show: true, message: `${vocab.FullNoun} cancelled.`, type: "success" });
      setConfirm(null);
      await refresh();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Failed to cancel.", type: "error" });
    }
    setBusy(false);
  };

  const deleteSession = async () => {
    setBusy(true);
    try {
      await apiClient.delete(`${vocab.apiBase}/sessions/${id}`);
      setToast({ show: true, message: `${vocab.FullNoun} deleted.`, type: "success" });
      setConfirm(null);
      navigate(routeBase);
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Failed to delete.", type: "error" });
    }
    setBusy(false);
  };

  if (loading) {
    return (
      <div className="demo-detail">
        <EmptyState title="Loading…" description={`Fetching ${vocab.fullNoun} details.`} />
      </div>
    );
  }
  if (!session) {
    return (
      <div className="demo-detail">
        <EmptyState
          title={`${vocab.FullNoun} not found`}
          description="It may have been deleted."
          action={<AppButton variant="default" onClick={() => navigate(routeBase)}>Back to list</AppButton>}
        />
      </div>
    );
  }

  const isLive = session.status === "live";
  const isScheduled = session.status === "scheduled";
  const totalLeadPages = Math.max(1, Math.ceil(leadsTotal / PAGE_SIZE));
  const interestedCount = leads.filter((l) => l.postSession?.interested === "yes").length;
  const interestedPct = leads.length ? Math.round((interestedCount / leads.length) * 100) : 0;
  // Time actually present; rows from before that was measured fall back to the span.
  const presentSeconds = (l) => l.attendance?.attendedSeconds ?? l.attendance?.durationSeconds ?? 0;
  const avgDuration = leads.length
    ? Math.round(leads.reduce((s, l) => s + presentSeconds(l), 0) / leads.length)
    : 0;
  const isWebinar = vocab.kind === "webinar";
  // Only Grats Meet recordings reach the LMS; a Zoom session has nothing to show.
  const hasRecordingTab = session.meetingProvider === "grats";

  return (
    <div className="demo-detail">
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <PageHeader
        backButton
        icon={PlayCircle}
        title={session.title}
        description={
          <span className="demo-detail-status">
            <StatusBadge status={session.status} domain="demoSession" />
            <span className="demo-detail-status__when">· {formatDateTime(session.scheduledStartAt)}</span>
          </span>
        }
        actions={
          <>
            {isScheduled && canRun && (
              <AppButton variant="default" onClick={startSession} disabled={busy}>
                <PlayCircle size={16} /> Start {vocab.noun}
              </AppButton>
            )}
            {isLive && (
              <>
                <AppButton variant="default" onClick={() => navigate(`${routeBase}/${id}/host`)}>
                  <Video size={16} /> Open host console
                </AppButton>
                {canRun && (
                  <AppButton variant="destructive" onClick={() => setConfirm({ type: "end" })} disabled={busy}>
                    <StopCircle size={16} /> End {vocab.noun}
                  </AppButton>
                )}
              </>
            )}
            {(isScheduled || isLive) && canRun && (
              <AppButton variant="ghost" onClick={() => setConfirm({ type: "cancel" })} disabled={busy}>
                Cancel
              </AppButton>
            )}
            {!isInstructor && canManage && (
              <AppButton variant="outline" onClick={() => navigate(`${routeBase}/${id}/edit`)}>
                <Edit size={16} /> Edit
              </AppButton>
            )}
            {!isInstructor && canManage && (
              <AppButton variant="ghost" onClick={() => setConfirm({ type: "delete" })}>
                <Trash2 size={16} /> Delete
              </AppButton>
            )}
          </>
        }
      />

      {/* The page on the institute's own website, when the webinar is listed there. */}
      {websiteUrl && (
        <div className="demo-detail__hero">
          <div className="demo-detail__hero-meta">
            <span className="demo-detail-link-meta">Event page on your website</span>
            <span className="demo-detail__public-url">{websiteUrl}</span>
          </div>
          <div className="demo-detail__hero-actions">
            <AppButton variant="outline" onClick={() => { navigator.clipboard?.writeText(websiteUrl); notify("Website link copied."); }}>
              <Copy size={14} /> Copy
            </AppButton>
            <AppButton variant="outline" as="a" href={websiteUrl} target="_blank" rel="noopener noreferrer">
              <Globe size={14} /> Open
            </AppButton>
          </div>
        </div>
      )}

      {/* Public link bar */}
      <div className="demo-detail__hero">
        <div className="demo-detail__hero-meta">
          <span className="demo-detail-link-meta">Public registration link</span>
          <span className="demo-detail__public-url">{publicUrl}</span>
        </div>
        <div className="demo-detail__hero-actions">
          <AppButton variant="outline" onClick={copyPublicLink}>
            <Copy size={14} /> Copy
          </AppButton>
          <AppButton variant="outline" as="a" href={publicUrl} target="_blank" rel="noopener noreferrer">
            <ExternalLink size={14} /> Open
          </AppButton>
          <AppButton variant="outline" as="a" href={calendarUrl} target="_blank" rel="noopener noreferrer">
            <CalendarPlus size={14} /> Add to calendar
          </AppButton>
        </div>
      </div>

      {/* Stats */}
      <StatsGrid className="demo-detail__stats">
        <StatsCard icon={Users}    title="Registered"      value={session.registrantCount || 0} iconColor="blue" />
        <StatsCard icon={Activity} title="Joined"          value={session.joinedCount || 0}     iconColor="emerald" />
        <StatsCard icon={Clock}    title="Avg watch time"  value={formatDuration(avgDuration)}  iconColor="violet" />
        <StatsCard icon={MessageSquare} title="Willing to join" value={`${interestedPct}%`}          iconColor="rose" />
      </StatsGrid>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="registrants">Registrants ({leadsTotal})</TabsTrigger>
          {isWebinar && <TabsTrigger value="attendance">Attendance &amp; certificates</TabsTrigger>}
          {isWebinar && <TabsTrigger value="automation">Automation</TabsTrigger>}
          {hasRecordingTab && <TabsTrigger value="recording">Recording</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview">
          <div className="demo-detail__section">
            <DetailSection title="Session details">
              <DetailRow icon={Hash}  label="Public slug"  value={session.publicSlug} />
              <DetailRow icon={Clock} label="Start"        value={formatDateTime(session.scheduledStartAt)} />
              <DetailRow icon={Clock} label="End"          value={formatDateTime(session.scheduledEndAt)} />
              <DetailRow icon={MapPin} label="Timezone"    value={session.timezone} />
              <DetailRow label="Description" value={session.description || "—"} />
              <DetailRow
                label={vocab.kind === "webinar" ? "Speaker" : "Instructor"}
                value={session.instructorId?.name || "—"}
              />
              {/* Only shown when there are any: an empty "Additional speakers —"
                  row on every demo class is noise on the common case. */}
              {(session.coHostInstructorIds || []).length > 0 && (
                <DetailRow
                  label={vocab.kind === "webinar" ? "Additional speakers" : "Additional instructors"}
                  value={session.coHostInstructorIds
                    .map((c) => c?.name || "Unknown")
                    .join(", ")}
                />
              )}
              <DetailRow
                label="Attendees can"
                value={describeAttendeePolicy(session.attendeePolicy)}
              />
              <DetailRow label="Linked batch"  value={session.batchId?.name || "—"} />
              <DetailRow label="Linked course" value={session.courseId?.name || "—"} />
            </DetailSection>
          </div>

          <div className="demo-detail__section">
            <DetailSection title="Capacity & form">
              <DetailRow label="Max attendees"          value={session.maxAttendees ?? "Unlimited"} />
              <DetailRow label="Join opens (min before)" value={`${session.joinOpensMinutesBefore} minutes`} />
              <DetailRow label="Allow late joiners"     value={session.allowJoinAfterStart ? "Yes" : "No"} />
              <DetailRow label="Attendee access"        value={session.registrationMode === "guest" ? "Guest (name only)" : "Pre-registration required"} />
              {session.registrationMode !== "guest" && (
                <>
                  <DetailRow label="Collect email"          value={session.collectEmail ? "Yes" : "No"} />
                  <DetailRow label="Collect city"           value={session.collectCity ? "Yes" : "No"} />
                </>
              )}
              <DetailRow label="Published"              value={session.isPublished ? "Yes" : "No"} />
              {session.registrationMode !== "guest" && (
                <DetailRow label="Consent text"           value={session.consentText} />
              )}
            </DetailSection>
          </div>

          {(session.zoomMeetingId || session.zoomMeetingCreatedAt) && (
            <div className="demo-detail__section">
              {/* Titled by the provider that actually hosted it — a Grats Meet
                  session has a room id here, not a Zoom meeting id. */}
              <DetailSection title={session.meetingProvider === "grats" ? "Meeting room" : "Zoom meeting"}>
                <DetailRow label="Meeting ID" value={session.zoomMeetingId || "—"} />
                <DetailRow label="Created at" value={formatDateTime(session.zoomMeetingCreatedAt)} />
                <DetailRow label="Ended at"   value={formatDateTime(session.zoomMeetingEndedAt)} />
              </DetailSection>
            </div>
          )}
        </TabsContent>

        <TabsContent value="registrants">
          <div className="demo-detail__section demo-detail__section--flush">
            <div className="demo-detail__registrants-toolbar">
              <AppButton variant="outline" size="sm" onClick={downloadCsv} disabled={!leadsTotal}>
                <Download size={14} /> Export CSV
              </AppButton>
            </div>
            {leadsLoading ? (
              <EmptyState title="Loading…" description="Fetching registrants." />
            ) : leads.length === 0 ? (
              <EmptyState title="No registrants yet" description="Share the public link to start collecting registrations." />
            ) : (
              <div className="demo-classes-page__table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Mobile</th>
                      <th>Email</th>
                      <th>City</th>
                      <th>Joined</th>
                      <th>Watch time</th>
                      <th>Feedback</th>
                      <th>Comment</th>
                      <th>CRM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((l) => (
                      <tr key={l._id}>
                        <td>
                          {l.name || "—"}
                          {l.isGuest && <span className="demo-detail-empty"> · Guest</span>}
                        </td>
                        <td>{l.isGuest ? "—" : (l.mobileE164 || l.mobile || "—")}</td>
                        <td>{l.email || "—"}</td>
                        <td>{l.city || "—"}</td>
                        <td>{l.attendance?.joinedAt ? formatDateTime(l.attendance.joinedAt) : "—"}</td>
                        <td>{formatDuration(presentSeconds(l))}</td>
                        <td>
                          {l.postSession?.interested ? (
                            <StatusBadge
                              status={l.postSession.interested === "yes" ? "active" : l.postSession.interested === "no" ? "destructive" : "pending"}
                            >
                              {{ yes: "Willing to join", maybe: "Need more info", no: "Not interested" }[l.postSession.interested] || l.postSession.interested}
                            </StatusBadge>
                          ) : <span className="demo-detail-empty">—</span>}
                        </td>
                        <td>
                          {l.postSession?.extraNotes
                            ? <span title={l.postSession.extraNotes}>{l.postSession.extraNotes.length > 60 ? l.postSession.extraNotes.slice(0, 60) + "…" : l.postSession.extraNotes}</span>
                            : <span className="demo-detail-empty">—</span>}
                        </td>
                        <td>
                          {l.isGuest ? (
                            <span className="demo-detail-empty">Guest</span>
                          ) : l.leadId ? (
                            <StatusBadge status="completed" dot>Synced</StatusBadge>
                          ) : canRun ? (
                            <AppButton variant="ghost" size="sm" onClick={() => convertLead(l._id)}>
                              <UserPlus size={14} /> Convert
                            </AppButton>
                          ) : (
                            <span className="demo-detail-empty">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Paginator
              page={leadsPage}
              totalPages={totalLeadPages}
              total={leadsTotal}
              pageSize={PAGE_SIZE}
              onChange={setLeadsPage}
              infoLabel="registrants"
            />
          </div>
        </TabsContent>

        {isWebinar && (
          <TabsContent value="attendance">
            <div className="demo-detail__section demo-detail__section--flush">
              <AttendancePanel
                apiBase={vocab.apiBase}
                sessionId={id}
                sessionStatus={session.status}
                noun={vocab.noun}
                canManage={!isInstructor && canManage}
                notify={notify}
              />
            </div>
          </TabsContent>
        )}
        {isWebinar && (
          <TabsContent value="automation">
            <div className="demo-detail__section demo-detail__section--flush">
              <AutomationPanel apiBase={vocab.apiBase} sessionId={id} canManage={!isInstructor && canManage} notify={notify} refreshKey={session.status} />
            </div>
          </TabsContent>
        )}
        {hasRecordingTab && (
          <TabsContent value="recording">
            <div className="demo-detail__section">
              <RecordingsPanel
                apiBase={vocab.apiBase}
                sessionId={id}
                sessionStatus={session.status}
                noun={vocab.noun}
                canSync={canRun}
                canDelete={!isInstructor && canManage}
                notify={notify}
              />
            </div>
          </TabsContent>
        )}
      </Tabs>

      <ConfirmModal
        show={confirm?.type === "cancel"}
        onHide={() => setConfirm(null)}
        onConfirm={cancelSession}
        title={`Cancel this ${vocab.noun}?`}
        message={`Registrants will see a 'cancelled' message. The ${vocab.noun} cannot be re-opened.`}
        confirmText="Yes, cancel"
        cancelText="Keep it"
        variant="warning"
        loading={busy}
      />
      <ConfirmModal
        show={confirm?.type === "end"}
        onHide={() => setConfirm(null)}
        onConfirm={endSession}
        title={`End this ${vocab.noun} now?`}
        message="The live room will be closed and the host slot freed up."
        confirmText={`End ${vocab.noun}`}
        cancelText="Keep running"
        variant="warning"
        loading={busy}
      />
      <ConfirmModal
        show={confirm?.type === "delete"}
        onHide={() => setConfirm(null)}
        onConfirm={deleteSession}
        title={`Delete this ${vocab.noun}?`}
        message={`Registrant data will remain in the database for reporting. Any recording of this ${vocab.noun} is deleted for good.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={busy}
      />
    </div>
  );
};

export default DemoClassesDetailPage;
